<template>
  <van-popup
    v-model="visible"
    class="checkin-popup"
    :close-on-click-overlay="false"
    get-container="body"
  >
    <!-- 选择打卡时间 -->
    <div class="checkin-view view-confirm" v-show="mode === 'confirm'">
      <h2 class="dialog-title">打卡签到</h2>
      <div class="task-body">
        <h3 class="task-name">{{ task.taskType }} {{ task.name }}</h3>
        <p class="task-description">{{ task.description }}</p>
      </div>
      <div class="date-label">选择打卡时间：</div>
      <div class="date-picker" @click="dateVisible = true">
        <span class="date-value">{{ dateText }}</span>
        <svg class="calendar" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="2.5" y="4.5" width="19" height="17" rx="1.5" stroke="currentColor" stroke-width="1.7"/>
          <path d="M2.5 9h19M7 2.5v4M17 2.5v4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
        </svg>
      </div>
      <div class="form-actions">
        <button class="button button-neutral" type="button" @click="close">取消</button>
        <button class="button button-red" type="button" :disabled="submitting" @click="confirmCheckin">{{ submitting ? '提交中' : '确认打卡' }}</button>
      </div>
    </div>
    <!-- 打卡成功勋章 -->
    <div class="checkin-view view-success" :class="{ 'is-playing': playing }" v-show="mode === 'success'">
      <h2 class="dialog-title success-title">打卡成功！恭喜获得勋章！</h2>
      <div class="medal-stage" ref="medalStage">
        <div class="halo"></div>
        <div class="rays"></div>
        <div class="ring"></div>
        <div class="medal-wrap">
          <img class="medal-art" v-if="task.medalImage" :src="task.medalImage" :alt="task.medalName || '打卡勋章'">
          <div class="medal-shine" v-if="task.medalImage" :style="shineStyle"></div>
        </div>
        <i class="sparkle one"></i>
        <i class="sparkle two"></i>
        <i class="sparkle three"></i>
        <i class="sparkle four"></i>
        <div class="particle-layer">
          <i
            class="particle"
            :class="{ paper: item.paper }"
            v-for="item in particles"
            :key="item.id"
            :style="item.style"
          ></i>
        </div>
      </div>
      <h3 class="medal-name">{{ task.medalName }}</h3>
      <button class="button button-red success-action" type="button" @click="close">确定</button>
    </div>
    <van-popup v-model="dateVisible" position="bottom" get-container="body">
      <van-datetime-picker
        v-model="currentDate"
        type="date"
        title="选择打卡时间"
        :min-date="minDate"
        :max-date="maxDate"
        @confirm="onDateConfirm"
        @cancel="dateVisible = false"
      />
    </van-popup>
  </van-popup>
</template>

<script>
import { checkinResearchCampPlanItem } from '@/api/researchCamp'

export default {
  name: 'CampCheckin',
  data () {
    return {
      task: {
        id: null,
        taskType: '',
        name: '',
        description: '',
        medalName: '',
        medalImage: ''
      },
      visible: false,
      dateVisible: false,
      mode: 'confirm',
      submitting: false,
      playing: false,
      particles: [],
      particleTimer: null,
      currentDate: new Date(),
      minDate: new Date(),
      maxDate: new Date()
    }
  },
  computed: {
    // 当前选择的打卡日期
    dateText () {
      return this.formatDate(this.currentDate)
    },
    // 勋章扫光遮罩
    shineStyle () {
      if (!this.task.medalImage) return {}
      const mask = `url(${this.task.medalImage}) center / contain no-repeat`
      return {
        mask: mask,
        webkitMask: mask
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.particleTimer)
  },
  methods: {
    // 从步骤列表打开某一条打卡
    openTask (task, entryDate) {
      this.task = {
        id: task.id,
        taskType: task.title,
        name: task.sub,
        description: task.desc,
        medalName: task.medalName,
        medalImage: task.medalImage,
        minCheckinDate: task.minCheckinDate
      }
      const minimumDate = this.parseDate(task.minCheckinDate) || entryDate || new Date()
      this.minDate = new Date(minimumDate.getTime())
      this.minDate.setHours(0, 0, 0, 0)
      this.maxDate = new Date()
      this.maxDate.setHours(23, 59, 59, 999)
      this.currentDate = new Date()
      this.currentDate.setHours(0, 0, 0, 0)
      if (this.currentDate < this.minDate) this.currentDate = new Date(this.minDate.getTime())
      this.submitting = false
      this.mode = 'confirm'
      this.visible = true
    },
    // 关闭打卡弹窗
    close () {
      this.visible = false
      this.playing = false
      this.particles = []
      clearTimeout(this.particleTimer)
    },
    // 解析打卡日期
    parseDate (value) {
      if (!value) return null
      if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate())
      if (Array.isArray(value) && value.length >= 3) {
        const year = Number(value[0])
        const month = Number(value[1])
        const day = Number(value[2])
        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
        return new Date(year, month - 1, day)
      }
      const raw = String(value)
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const parts = raw.split('-').map(Number)
        return new Date(parts[0], parts[1] - 1, parts[2])
      }
      if (/^\d+$/.test(raw)) {
        const date = new Date(Number(raw))
        return new Date(date.getFullYear(), date.getMonth(), date.getDate())
      }
      return null
    },
    // 确认打卡后切到勋章动效
    async confirmCheckin () {
      if (!this.task.id || this.submitting) return
      this.submitting = true
      try {
        const response = await checkinResearchCampPlanItem(this.task.id, {
          checkinDate: this.formatDate(this.currentDate)
        })
        const result = response && response.data ? response.data : {}
        this.task.medalName = result.medalName
        this.task.medalImage = result.medalImage
        this.mode = 'success'
        this.playMedal()
        this.$emit('done', { id: this.task.id, date: this.formatDate(this.currentDate) })
      } catch (error) {
        this.$toast(error && error.msg ? error.msg : '打卡失败，请重试')
      } finally {
        this.submitting = false
      }
    },
    // 确认选择的打卡日期
    onDateConfirm (value) {
      this.currentDate = value
      this.dateVisible = false
    },
    // 打卡日期，展示和提交都用 -
    formatDate (date) {
      const month = date.getMonth() + 1
      const day = date.getDate()
      const pad = value => (value < 10 ? '0' + value : String(value))
      return [date.getFullYear(), pad(month), pad(day)].join('-')
    },
    // 播放勋章动效
    playMedal () {
      this.playing = false
      this.particles = []
      clearTimeout(this.particleTimer)
      this.$nextTick(() => {
        this.playing = true
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
        const stage = this.$refs.medalStage
        const radius = stage ? stage.clientWidth : 160
        const colors = ['#ffd05c', '#ffb33f', '#ffe7a4', '#f78c48']
        const particles = []
        for (let index = 0; index < 36; index++) {
          const angle = (index / 36) * Math.PI * 2
          const distance = radius * (0.44 + Math.random() * 0.37)
          particles.push({
            id: index,
            paper: index % 3 === 0,
            style: {
              '--x': `${Math.cos(angle) * distance}px`,
              '--y': `${Math.sin(angle) * distance * 0.72 + 18}px`,
              '--rotation': `${Math.random() * 480 - 240}deg`,
              '--delay': `${0.22 + Math.random() * 0.18}s`,
              '--particle-color': colors[index % colors.length]
            }
          })
        }
        this.particles = particles
        this.particleTimer = setTimeout(() => {
          this.particles = []
        }, 1800)
      })
    }
  }
}
</script>

<style lang="less" scoped>
.checkin-popup {
  display: flex;
  flex-direction: column;
  width: 84%;
  max-height: 80vh;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;

  .view-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    min-height: 0;
    max-height: 80vh;
    padding: 20px 16px 24px;
    overflow-y: auto;
  }

  .view-confirm {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    min-height: 0;
    max-height: 80vh;
    padding: 20px 16px 24px;
    overflow: hidden;

    .dialog-title,
    .date-label,
    .date-picker,
    .form-actions {
      flex-shrink: 0;
    }

    .task-body {
      flex: 1 1 auto;
      min-height: 0;
      margin-top: 18px;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .task-name {
      margin: 0;
      color: #080808;
      font-size: 14px;
      font-weight: 400;
      line-height: 21px;
    }

    .task-description {
      margin: 2px 0 0;
      color: #8c817b;
      font-size: 12px;
      line-height: 18px;
    }

    .date-label {
      margin-top: 16px;
      color: #8c817b;
      font-size: 12px;
      line-height: 18px;
    }

    .date-picker {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 37px;
      margin-top: 12px;
      padding: 0 10px;
      border: 1px solid #e6e6e6;
      border-radius: 8px;
      box-sizing: border-box;

      .date-value {
        color: #111;
        font-size: 14px;
      }

      .calendar {
        width: 18px;
        height: 18px;
        color: #c2c2c5;
      }
    }

    .form-actions {
      display: grid;
      grid-template-columns: 1.03fr 1fr;
      gap: 12px;
      margin-top: 20px;
    }
  }

  .dialog-title {
    margin: 0;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: #1c1c1c;
  }

  .button {
    height: 38px;
    border: 0;
    border-radius: 38px;
    font-size: 14px;
    line-height: 1;
  }

  .button-neutral {
    background: #f1efee;
    color: #8c817b;
  }

  .button-red {
    background: #f01800;
    color: #fff;
    font-weight: 500;
  }

  .success-title {
    align-self: stretch;
    color: #ff2c18;
    white-space: nowrap;
  }

  .medal-stage {
    position: relative;
    width: 160px;
    height: 105px;
    margin-top: 16px;
    isolation: isolate;
    perspective: 650px;
  }

  .medal-wrap {
    position: relative;
    width: 109px;
    height: 97px;
    margin: 3px auto 0;
    transform-origin: 50% 58%;
  }

  .medal-art {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .medal-shine {
    position: absolute;
    inset: 0;
    background: linear-gradient(110deg, transparent 34%, rgba(255, 255, 255, 0.88) 46%, rgba(255, 255, 255, 0.12) 54%, transparent 65%);
    opacity: 0;
  }

  .medal-name {
    align-self: stretch;
    margin: 8px 0 0;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    line-height: 21px;
    color: #1c1c1c;
  }

  .success-action {
    align-self: stretch;
    width: 100%;
    margin-top: 20px;
  }

  .halo,
  .rays,
  .ring {
    position: absolute;
    z-index: -1;
    opacity: 0;
  }

  .halo {
    inset: -26px -8px;
    border-radius: 50%;
    background: radial-gradient(circle, #fff1b8 0%, rgba(255, 217, 124, 0.32) 28%, rgba(255, 228, 157, 0.05) 55%, transparent 70%);
  }

  .rays {
    inset: -26px;
    border-radius: 50%;
    background: repeating-conic-gradient(from 0deg, transparent 0deg 13deg, rgba(255, 200, 71, 0.27) 14deg 18deg, transparent 19deg 30deg);
    mask: radial-gradient(circle, transparent 20%, #000 38%, transparent 69%);
  }

  .ring {
    top: 50%;
    left: 50%;
    width: 94px;
    height: 94px;
    margin: -47px;
    border: 2px solid #ffe3a0;
    border-radius: 50%;
  }

  .sparkle {
    position: absolute;
    width: 10px;
    height: 10px;
    background: #ffcf67;
    clip-path: polygon(50% 0, 63% 38%, 100% 50%, 63% 62%, 50% 100%, 37% 62%, 0 50%, 37% 38%);
    opacity: 0;

    &.one {
      top: 15px;
      left: 8px;
    }

    &.two {
      top: -4px;
      right: 21px;
      width: 8px;
      height: 8px;
    }

    &.three {
      bottom: 4px;
      right: 8px;
      width: 11px;
      height: 11px;
    }

    &.four {
      bottom: -4px;
      left: 26px;
      width: 6px;
      height: 6px;
    }
  }

  .particle-layer {
    position: absolute;
    z-index: 2;
    inset: -45px;
    pointer-events: none;
  }

  .particle {
    position: absolute;
    left: 50%;
    top: 48%;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--particle-color);
    opacity: 0;
    animation: particle-burst 1.25s cubic-bezier(0.15, 0.65, 0.32, 1) var(--delay) both;

    &.paper {
      width: 5px;
      height: 8px;
      border-radius: 1px;
    }
  }

  .view-success.is-playing {
    .success-title {
      animation: text-reveal 0.45s 0.08s both;
    }

    .medal-wrap {
      animation: medal-reveal 1.05s cubic-bezier(0.16, 0.72, 0.25, 1) both;
    }

    .medal-shine {
      animation: shine 1s 0.65s ease-in-out both;
    }

    .halo {
      animation: halo-reveal 2s 0.12s ease-out both;
    }

    .rays {
      animation: rays-reveal 1.9s 0.15s ease-out both;
    }

    .ring {
      animation: ring-expand 0.95s 0.28s ease-out both;
    }

    .sparkle {
      animation: sparkle 1.25s 0.45s ease-in-out both;

      &.two {
        animation-delay: 0.7s;
      }

      &.three {
        animation-delay: 0.8s;
      }

      &.four {
        animation-delay: 0.55s;
      }
    }

    .medal-name {
      animation: text-reveal 0.45s 0.45s both;
    }

    .success-action {
      animation: text-reveal 0.4s 0.6s both;
    }
  }
}

@keyframes medal-reveal {
  0% {
    opacity: 0;
    transform: translateY(18px) scale(0.2) rotateY(-55deg) rotate(-12deg);
    filter: brightness(1.8) drop-shadow(0 0 18px #ffd86e);
  }
  48% {
    opacity: 1;
    transform: translateY(-4px) scale(1.15) rotateY(10deg) rotate(4deg);
    filter: brightness(1.12) drop-shadow(0 0 10px #ffe1a1);
  }
  72% {
    transform: translateY(1px) scale(0.96) rotateY(-5deg) rotate(-1deg);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1) rotateY(0) rotate(0);
    filter: brightness(1);
  }
}

@keyframes text-reveal {
  from {
    opacity: 0;
    transform: translateY(7px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes shine {
  0% {
    opacity: 0;
    background-position: -80px 0;
  }
  25% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    background-position: 120px 0;
  }
}

@keyframes halo-reveal {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }
  28% {
    opacity: 0.9;
    transform: scale(1.05);
  }
  65% {
    opacity: 0.55;
  }
  100% {
    opacity: 0;
    transform: scale(1.15);
  }
}

@keyframes rays-reveal {
  0% {
    opacity: 0;
    transform: scale(0.6) rotate(-30deg);
  }
  25% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
    transform: scale(1.15) rotate(35deg);
  }
}

@keyframes ring-expand {
  0% {
    opacity: 0;
    transform: scale(0.35);
  }
  20% {
    opacity: 0.8;
  }
  100% {
    opacity: 0;
    transform: scale(1.7);
  }
}

@keyframes sparkle {
  0% {
    opacity: 0;
    transform: scale(0.1) rotate(-20deg);
  }
  35% {
    opacity: 1;
    transform: scale(1.2);
  }
  65% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
    transform: scale(0.4) rotate(35deg);
  }
}

@keyframes particle-burst {
  0% {
    opacity: 0;
    transform: translate(0, 0) scale(0.3);
  }
  12% {
    opacity: 1;
  }
  60% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translate(var(--x), var(--y)) rotate(var(--rotation)) scale(0.5);
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/components/CampCheckin.vue